/**
 * @fileoverview The Sales Team, Active Clients and User Management cards of the Sales Dashboard.
 * @description Three summary cards that each open their page. Sales Team shows the number of
 * people, tiles for the Head, Managers and Executives, the banks covered and the inactive count;
 * Active Clients shows the banks onboarded with a Live and Onboarding bar and the branches; User
 * Management shows the users provisioned with an Active and Inactive bar and, when the caller may
 * add users, an Add user link. Each card renders only when its section is present.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import SummaryCardShell from '@/shared/components/molecules/SummaryCardShell'
import { ROUTES } from '@/shared/constants/routes.constants'

import DashboardLegendStat from './DashboardLegendStat'
import SegmentedBar from './SegmentedBar'

import type {
  DashboardActiveClients,
  DashboardSalesTeam,
  DashboardUserManagement,
} from '@/features/sales/types/sales-dashboard.types'

/** @description The two-person glyph the Sales Team and User Management cards share. */
const PEOPLE_PATHS = (
  <>
    <circle cx="9" cy="8" r="3.3" />
    <path d="M3.2 20c0-3.2 2.6-5.4 5.8-5.4s5.8 2.2 5.8 5.4" />
    <path d="M17 11.4a2.9 2.9 0 100-5.8" />
    <path d="M18 20c0-2.5-1-4.2-2.5-5" />
  </>
)

interface TeamTileProps {
  /** Which role the tile counts (heads, managers or executives); the test ids carry it. */
  role: string
  label: string
  value: number
}

/**
 * @component
 * @param {TeamTileProps} props - One role's count.
 * @param {string} props.role - The role the tile counts.
 * @param {string} props.label - The role's name.
 * @param {number} props.value - How many people hold it.
 */
function TeamTile({ role, label, value }: TeamTileProps) {
  return (
    <div
      className="flex flex-col gap-0.5 rounded-md bg-input-bg px-2.5 py-2"
      data-testid={`dashboard-team-${role}`}
    >
      <span
        className="text-[17px] font-bold leading-none text-ink"
        data-testid={`dashboard-team-${role}-value`}
      >
        {value}
      </span>
      <span
        className="text-[11px] font-medium text-slate"
        data-testid={`dashboard-team-${role}-label`}
      >
        {label}
      </span>
    </div>
  )
}

/**
 * @component
 * @param {{ salesTeam: DashboardSalesTeam }} props - The Sales Team section.
 * @param {DashboardSalesTeam} props.salesTeam - The caller's team figures.
 */
export function SalesTeamCard({ salesTeam }: { salesTeam: DashboardSalesTeam }) {
  const { t } = useTranslation()
  const tiles = [
    { key: 'heads', label: t('sales.dashboard.cards.teamHeads'), value: salesTeam.heads },
    { key: 'managers', label: t('sales.dashboard.cards.teamManagers'), value: salesTeam.managers },
    {
      key: 'executives',
      label: t('sales.dashboard.cards.teamExecutives'),
      value: salesTeam.executives,
    },
  ]

  return (
    <SummaryCardShell
      to={ROUTES.SALES_TEAM}
      testId="dashboard-card-team"
      iconBg="bg-purple-bg"
      icon={
        <svg
          className="text-purple"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        >
          {PEOPLE_PATHS}
        </svg>
      }
      title={t('nav.salesTeam')}
      value={salesTeam.members}
      caption={t('sales.dashboard.cards.teamCaption')}
      footer={
        <>
          <span
            className="text-xs font-medium text-slate"
            data-testid="dashboard-team-banks-covered"
          >
            {t('sales.dashboard.cards.teamBanksCovered', { count: salesTeam.banksCovered })}
          </span>
          <span className="flex-1" />
          {salesTeam.inactive > 0 && (
            <span
              className="text-xs font-semibold text-footer"
              data-testid="dashboard-team-inactive"
            >
              {t('sales.dashboard.cards.teamInactive', { count: salesTeam.inactive })}
            </span>
          )}
        </>
      }
    >
      <div className="grid grid-cols-3 gap-2.5">
        {tiles.map((tile) => (
          <TeamTile key={tile.key} role={tile.key} label={tile.label} value={tile.value} />
        ))}
      </div>
    </SummaryCardShell>
  )
}

/**
 * @component
 * @param {{ activeClients: DashboardActiveClients }} props - The Active Clients section.
 * @param {DashboardActiveClients} props.activeClients - The clients' totals.
 */
export function ActiveClientsCard({ activeClients }: { activeClients: DashboardActiveClients }) {
  const { t } = useTranslation()

  return (
    <SummaryCardShell
      to={ROUTES.SALES_CLIENTS}
      testId="dashboard-card-clients"
      iconBg="bg-status-warning-bg"
      icon={
        <svg
          className="text-status-warning"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        >
          <rect x="3" y="7.5" width="18" height="12" rx="2.2" />
          <path d="M8.5 7.5V5.8a1.8 1.8 0 011.8-1.8h3.4a1.8 1.8 0 011.8 1.8v1.7" />
        </svg>
      }
      title={t('nav.activeClients')}
      value={activeClients.total}
      caption={t('sales.dashboard.cards.clientsCaption')}
      footer={
        <>
          <DashboardLegendStat
            label={t('sales.dashboard.cards.clientsLive')}
            count={activeClients.live}
            testId="dashboard-clients-live"
            toneClassName="text-success"
          />
          <DashboardLegendStat
            label={t('sales.dashboard.cards.clientsOnboarding')}
            count={activeClients.onboarding}
            testId="dashboard-clients-onboarding"
            toneClassName="text-status-warning"
          />
          <span className="flex-1" />
          <span
            className="text-xs font-semibold text-slate"
            data-testid="dashboard-clients-branches"
          >
            {t('sales.dashboard.cards.clientsBranches', { count: activeClients.branches })}
          </span>
        </>
      }
    >
      <SegmentedBar
        testId="dashboard-clients-bar"
        segments={[
          {
            id: 'live',
            label: t('sales.dashboard.cards.clientsLive'),
            value: activeClients.live,
            color: 'bg-success',
          },
          {
            id: 'onboarding',
            label: t('sales.dashboard.cards.clientsOnboarding'),
            value: activeClients.onboarding,
            color: 'bg-status-warning',
          },
        ]}
      />
    </SummaryCardShell>
  )
}

interface UsersCardProps {
  userManagement: DashboardUserManagement
  /** Whether the caller may add users (the Add user link shows only then). */
  canAddUsers: boolean
}

/**
 * @component
 * @param {UsersCardProps} props - The User Management section and whether to offer Add user.
 * @param {DashboardUserManagement} props.userManagement - The users' totals.
 * @param {boolean} props.canAddUsers - Whether the caller may add users.
 */
export function UsersCard({ userManagement, canAddUsers }: UsersCardProps) {
  const { t } = useTranslation()

  return (
    <SummaryCardShell
      to={ROUTES.SALES_USERS}
      testId="dashboard-card-users"
      iconBg="bg-neutral-bg"
      icon={
        <svg
          className="text-neutral-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        >
          {PEOPLE_PATHS}
        </svg>
      }
      title={t('nav.userManagement')}
      value={userManagement.total}
      caption={t('sales.dashboard.cards.usersCaption')}
      footer={
        <>
          <DashboardLegendStat
            label={t('sales.dashboard.cards.usersActive')}
            count={userManagement.active}
            testId="dashboard-users-active"
            toneClassName="text-success"
          />
          <DashboardLegendStat
            label={t('sales.dashboard.cards.usersInactive')}
            count={userManagement.inactive}
            testId="dashboard-users-inactive"
            toneClassName="text-footer"
          />
          <span className="flex-1" />
          {canAddUsers && (
            <span
              className="text-xs font-semibold text-primary"
              data-testid="dashboard-users-add-link"
            >
              {t('sales.dashboard.cards.usersAdd')}
            </span>
          )}
        </>
      }
    >
      <SegmentedBar
        testId="dashboard-users-bar"
        segments={[
          {
            id: 'active',
            label: t('sales.dashboard.cards.usersActive'),
            value: userManagement.active,
            color: 'bg-success',
          },
          {
            id: 'inactive',
            label: t('sales.dashboard.cards.usersInactive'),
            value: userManagement.inactive,
            color: 'bg-inactive',
          },
        ]}
      />
    </SummaryCardShell>
  )
}
